import { CheckIcon } from '@chakra-ui/icons';
import { Button, Grid, Stack, useBoolean } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { TotpCodeDisplay, TotpResetButton } from 'components/TotpCodeDisplay';
import { simpleCheckBox, simpleInput } from 'helpers/makeFormFields';
import { ContentCard } from 'layout/ContentCard';
import { map, property } from 'lodash';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IoIosCopy } from 'react-icons/io';
import { useCopyToClipboard } from 'usehooks-ts';
import * as yup from 'yup';

import AddonIconButton from './form-elements/AddonIconButton';
import AlertModal from './modals/AlertModal';

export const AccountCredentialsCard = ({ account, sendRequest }: { account: any; sendRequest(x: object): Promise<void> }) => {
  const [value, copy] = useCopyToClipboard();
  const [showCheckIcon, setShowCheckIcon] = useBoolean(false);

  const SHOW_CHECKICON_DURATION = 700;
  const { t } = useTranslation();
  const [canEdit, setCanEdit] = useState(false);

  const isLoading = false;

  const schema = yup
    .object({
      username: yup.string().required(),
      password: yup
        .string()
        .nullable()
        .when('random_password', {
          is: false,
          then: (schema) => schema.required(),
        }),
      random_password: yup.boolean(),
      status: yup.number().required(),
    })
    .required();

  const {
    handleSubmit,
    formState: { errors, isValid, submitCount },
    register,
    setValue,
    setError,
    getValues,
    watch,
    reset,
  } = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: {
      status: account?.status,
      //@ts-ignore
      portal_id: account?.portal?.id,
      workshop_id: account?.workshop_id,
      username: account?.username,
      password: account?.password,
      random_password: false,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const randomPassword = watch('random_password');

  useEffect(() => {
    reset({
      status: account?.status,
      portal_id: account?.portal?.id,
      workshop_id: account?.workshop_id,
      username: account?.username,
      password: account?.password,
      random_password: randomPassword,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const { mutate, isPending: isLoadingForm } = useMutation<any, Error, any>({
    mutationFn: async (data) => {
      // @ts-ignore
      if (account?.status > 0 && !data?.force) {
        NiceModal.show(AlertModal as any, {
          onSubmit: () => mutate({ ...getValues(), force: true }),
          children: t('workshop:credentials.modal.description', {
            make: map(account?.makes, property('name')).join(' / '),
          }),
          onCancel: () => null,
          content: {
            header: t('workshop:credentials.modal.header', {
              make: map(account?.makes, property('name')).join(' / '),
            }),
            footer: {
              buttons: {
                cancelCaption: t('common:abort'),
                actionCaption: t('workshop:credentials.modal.submit'),
              },
            },
          },
        });
        return;
      } else {
        setValue('random_password', false);
        return sendRequest(data as any).then(() => setCanEdit(false));
      }
    },
  });

  const onSubmit: SubmitHandler<any> = (data) => {
    mutate(data, {
      onError: (error: any) => {
        // manageErrorResponse(toast, error.response);
        const { errors } = error.response.data;

        (Object.keys(errors) as Array<keyof any>).forEach((key) => {
          setError(String(key), {
            type: 'server',
            message: errors[key]!.join('. '),
          });
        });
      },
    });
  };

  return (
    <ContentCard header={t('pages:account.credentials.title')} as="form" opacity={isLoadingForm ? 0.5 : 1} onSubmit={handleSubmit(onSubmit)}>
      <Grid w="full" templateColumns={'2fr 1.5fr'} gap={4} rowGap={9} autoFlow={'dense'}>
        <Stack spacing={4}>
          <Grid gap={4}>
            <Stack direction={'row'} spacing={4} alignItems={'flex-end'}>
              {simpleInput({
                preventLastPassAutoFill: true,
                name: 'username',
                label: t('components:credential_card.username.label'),
                register,
                errors,
                disabled: !canEdit,
                noAddonSpacing: true,
                rightAddon: (
                  <AddonIconButton
                    icon={value === account?.username && showCheckIcon ? <CheckIcon /> : <IoIosCopy />}
                    aria-label={'copy credentials username button'}
                    onClick={() => {
                      setShowCheckIcon.on();
                      copy(account?.username);
                      setTimeout(setShowCheckIcon.off, SHOW_CHECKICON_DURATION);
                    }}
                  />
                ),
              })}
            </Stack>

            <Grid gap={2}>
              <Stack direction={'row'} spacing={4} alignItems={'flex-end'}>
                {simpleInput({
                  preventLastPassAutoFill: true,
                  name: 'password',
                  label: t('components:credential_card.password.label'),
                  register,
                  errors: randomPassword ? null : errors,
                  disabled: !canEdit || randomPassword,
                  noAddonSpacing: true,
                  rightAddon: (
                    <AddonIconButton
                      icon={value === account?.password && showCheckIcon ? <CheckIcon /> : <IoIosCopy />}
                      disabled={canEdit}
                      aria-label={'copy accounts password button'}
                      onClick={() => {
                        setShowCheckIcon.on();
                        copy(account?.password);
                        setTimeout(setShowCheckIcon.off, SHOW_CHECKICON_DURATION);
                      }}
                    />
                  ),
                })}
              </Stack>
              {canEdit &&
                simpleCheckBox({
                  name: 'random_password',
                  label: t('components:credential_card.random_password.label'),
                  register,
                  errors,
                  disabled: !canEdit,
                })}
            </Grid>

            {account?.has_totp_secret && account?.portal?.requires_totp && (
              <TotpCodeDisplay account_id={account.id} hasTotpSecret={!!account?.has_totp_secret} />
            )}

            <Grid w="full" templateColumns={'.5fr .5fr'} gap={4} rowGap={9} autoFlow={'column'}>
              <Button
                variant="primary"
                size="sm"
                bg={canEdit ? 'red.500' : 'orange'}
                _hover={{
                  bg: canEdit ? 'red.600' : 'orange.400',
                }}
                onClick={() => {
                  if (canEdit) {
                    reset({
                      status: account?.status,
                      //@ts-ignore
                      portal_id: account?.portal?.id,
                      workshop_id: account?.workshop_id,
                      username: account?.username,
                      password: account?.password,
                      random_password: false,
                    });
                  }
                  setCanEdit(!canEdit);
                }}
              >
                {canEdit ? t('common:abort') : t('common:edit')}
              </Button>

              {canEdit && (
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  bg={'blue.500'}
                  _hover={{
                    bg: 'blue.600',
                  }}
                  data-test-id="credentials-submit-button"
                  disabled={(submitCount! > 0 && !isValid) || isLoading}
                >
                  {t('common:submit')}
                </Button>
              )}

              {account?.has_totp_secret && account?.portal?.requires_totp && <TotpResetButton account_id={account.id} />}
            </Grid>
          </Grid>
        </Stack>
      </Grid>
    </ContentCard>
  );
};
